import { useCallback, useMemo, useEffect } from 'react'
import * as React from 'react'
import { useIntl } from 'react-intl'
import BodyText from 'LuxKit/Typography/BodyText'
import Group from 'components/utils/Group'
import Heading from 'LuxKit/Typography/Heading'
import LineUsdCircleIcon from 'LuxKit/Icons/line/LineUsdCircleIcon'
import LineBalanceScaleIcon from 'LuxKit/Icons/line/LineBalanceScaleIcon'
import LineWalletIcon from 'LuxKit/Icons/line/LineWalletIcon'
import TileCard from 'tripPlanner/components/EntryPoints/Common/Tile/TileCard'
import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import TileCardsGroup from './Common/Tile/TileCardsGroup'
import { arrayToMap } from 'lib/array/arrayUtils'
import { trackClientEvent } from 'analytics/analytics'

interface Props {
  activityBudget: Array<{
    activityBudget: string
    selected: boolean
  }>
  onBudgetSelect: (budget: string) => void
  onNext: () => void
  isGeneratingTrip: boolean
  stepNumber: number
  totalSteps: number
}

function AITripBudgetSelection({
  activityBudget,
  onBudgetSelect,
  onNext,
  isGeneratingTrip,
  stepNumber,
  totalSteps,
}: Props) {
  const intl = useIntl()
  const handleOnClick = useCallback((budget: string) => {
    onBudgetSelect(budget)
    onNext()
  }, [onBudgetSelect, onNext])

  const tripBudgetMap = useMemo(() =>
    arrayToMap(activityBudget, budget => budget.activityBudget, budget => budget.selected),
  [activityBudget])

  useEffect(() => {
    trackClientEvent({
      subject: 'activity_budget',
      action: 'view',
      category: 'trip_planner',
      type: 'nonInteraction',
    })
  }, [])

  const TRIP_BUDGETS = [
    {
      activityBudget: 'A Little Splash',
      title: intl.formatMessage({
        id: 'J9jwe5p8YBfDw0GNIlrR',
        defaultMessage: 'A Little Splash',
        description: 'Title for budget-conscious activity budget option on the form',
      }),
      description: intl.formatMessage({
        id: 'gnm6nVAGNpIr8RET3CbX',
        defaultMessage: 'Save where I can, spend where it counts.',
        description: 'Description for budget-conscious activity budget option',
      }),
      icon: <LineUsdCircleIcon />,
    },
    {
      activityBudget: 'Mix & Match',
      title: intl.formatMessage({
        id: 'LHDsE52T44iztptijxpL',
        defaultMessage: 'Mix & Match',
        description: 'Heading title for moderate activity budget option in the trip planner setup wizard',
      }),
      description: intl.formatMessage({
        id: 'Xjckfpofie2YEnawDYPT',
        defaultMessage: 'Some paid fun, every other day is fine.',
        description: 'Description for moderate activity budget option on the form',
      }),
      icon: <LineBalanceScaleIcon />,
    },
    {
      activityBudget: 'Open Wallet',
      title: intl.formatMessage({
        id: 'jtufXv9BqkY5rYwOr2aR',
        defaultMessage: 'Open Wallet',
        description: 'Heading title for generous activity budget option in the trip planner setup wizard',
      }),
      description: intl.formatMessage({
        id: 'GCb4I-p8W5THBnexNInn',
        defaultMessage: 'If it sounds amazing, I’m in.',
        description: 'Description for generous activity budget option on the form',
      }),
      icon: <LineWalletIcon />,
    },
  ]

  const stepText = intl.formatMessage(
    {
      id: 'ewX_UMLdx4cN8zd8qwrw',
      defaultMessage: '{stepNumber} of {totalSteps}',
      description: 'Step indicator showing current step number and total steps',
    },
    { stepNumber, totalSteps },
  )

  const headingText = intl.formatMessage(
    {
      id: 'nFPo7YebDFTUM4M6hdP-',
      defaultMessage: 'Activity <i>Budget</i>',
      description: 'Heading text with italicized Budget in the trip planner setup wizard',
    },
    {
      i: (chunks: React.ReactNode) => <i key="italic">{chunks}</i>,
    },
  )

  const descriptionText = intl.formatMessage({
    id: '0ZvQz_ymKt46mCVhHyPi',
    defaultMessage: 'What’s your activity budget looking like? (Don’t worry — we’ll get to food shortly)',
    description: 'Question asking about activity budget preference in the trip planner setup wizard',
  })

  return (
    <Group direction="vertical" gap={20} desktopGap={24}>
      <VerticalSpacer gap={8}>
        <BodyText variant="large" weight="bold">{stepText}</BodyText>
        <Heading variant="heading1">
          {headingText}
        </Heading>
        <BodyText variant="large">
          {descriptionText}
        </BodyText>
      </VerticalSpacer>
      <TileCardsGroup>
        {TRIP_BUDGETS.map((budget) => (
          <TileCard
            key={budget.activityBudget}
            title={budget.title}
            subtitle={budget.description}
            icon={budget.icon}
            value={budget.activityBudget}
            onClick={handleOnClick}
            selected={!!tripBudgetMap.get(budget.activityBudget)}
            disabled={isGeneratingTrip}
          />
        ))}
      </TileCardsGroup>
    </Group>
  )
}

export default AITripBudgetSelection
